import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  TransferCertificateRequestMutationRequest,
  TransferCertificateRequestMutationResponse,
} from "../types/TransferCertificateRequest"

type TransferCertificateRequestClient = typeof client<
  TransferCertificateRequestMutationResponse,
  Error,
  TransferCertificateRequestMutationRequest
>
type TransferCertificateRequest = {
  data: TransferCertificateRequestMutationResponse
  error: Error
  request: TransferCertificateRequestMutationRequest
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<TransferCertificateRequestClient>>
  client: {
    parameters: Partial<Parameters<TransferCertificateRequestClient>[0]>
    return: Awaited<ReturnType<TransferCertificateRequestClient>>
  }
}
/**
 * @summary Transfer certificate request
 * @link /v1/certificate-request/transfer-certificate-request
 */
export function useTransferCertificateRequest(
  options: {
    mutation?: UseMutationOptions<
      TransferCertificateRequest["response"],
      TransferCertificateRequest["error"],
      TransferCertificateRequest["request"]
    >
    client?: TransferCertificateRequest["client"]["parameters"]
  } = {}
): UseMutationResult<
  TransferCertificateRequest["response"],
  TransferCertificateRequest["error"],
  TransferCertificateRequest["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    TransferCertificateRequest["response"],
    TransferCertificateRequest["error"],
    TransferCertificateRequest["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        TransferCertificateRequest["data"],
        TransferCertificateRequest["error"],
        TransferCertificateRequest["request"]
      >({
        method: "post",
        url: `/v1/certificate-request/transfer-certificate-request`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
